/**
 * cus-search-pulse.js
 * Search bar visual guidance: underline pulse + magnifier glow animation
 * Uses performance.now() for frame-accurate timing across all refresh rates
 */
(function () {
  'use strict';

  /* ── constants ─────────────────────────────────────────────────── */
  var BRAND_ORANGE = '#D95700';
  var BRAND_RGB = '217,87,0';
  var PHASE_1_DUR = 3000;   // underline pulse sweep
  var PHASE_2_DUR = 800;    // crossfade: pulse out, icon to orange
  var PHASE_3_DUR = 3500;   // single breath cycle
  var PHASE_3_COUNT = 3;    // breath repetitions
  var PHASE_4_DUR = 1500;   // icon fade back to grey
  var PHASE_5_DUR = 3000;   // quiet pause
  var TOTAL_DUR = PHASE_1_DUR + PHASE_2_DUR + (PHASE_3_DUR * PHASE_3_COUNT) + PHASE_4_DUR + PHASE_5_DUR;

  /* pulse wave width as fraction of container */
  var WAVE_WIDTH = 0.30;
  /* max right edge of wave: 100% — sweep all the way to magnifier */
  var WAVE_MAX = 0.90;

  /* ── helpers ───────────────────────────────────────────────────── */
  function easeInOutSine(t) {
    return 0.5 - 0.5 * Math.cos(Math.PI * t);
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  /* ── per-instance controller ───────────────────────────────────── */
  function initPulse(section) {
    var form = section.querySelector('.cus-search-header__search-form');
    var input = section.querySelector('.cus-search-header__search-input');
    var button = section.querySelector('.cus-search-header__search-button');
    if (!form || !input || !button) return;

    /*
     * Pulse overlay: a narrow band that slides across the underline.
     * Width = 30% of container, gradient fades head & tail to transparent.
     * Positioned absolutely at bottom of form (over the input border-bottom).
     */
    var pulse = document.createElement('div');
    pulse.className = 'cus-search-pulse__line';
    pulse.style.cssText =
      'position:absolute;bottom:0;height:2px;pointer-events:none;' +
      'width:' + (WAVE_WIDTH * 100) + '%;opacity:0;z-index:1;' +
      'background:linear-gradient(90deg,transparent 0%,' + BRAND_ORANGE + ' 35%,' + BRAND_ORANGE + ' 65%,transparent 100%);';
    form.appendChild(pulse);

    /* state */
    var paused = false;
    var rafId = null;
    var startTime = null;

    /* focus / blur — pause during user interaction */
    input.addEventListener('focus', function () {
      paused = true;
      resetVisuals();
    });
    input.addEventListener('blur', function () {
      paused = false;
      startTime = null; // restart cycle cleanly
    });

    function resetVisuals() {
      pulse.style.opacity = '0';
      button.style.color = '';
      button.style.filter = '';
      button.style.opacity = '';
    }

    /* ── animation loop ──────────────────────────────────────────── */
    function tick(now) {
      rafId = requestAnimationFrame(tick);
      if (paused) return;

      if (startTime === null) startTime = now;
      var elapsed = (now - startTime) % TOTAL_DUR;

      var p1End = PHASE_1_DUR;
      var p2End = p1End + PHASE_2_DUR;
      var p3End = p2End + PHASE_3_DUR * PHASE_3_COUNT;
      var p4End = p3End + PHASE_4_DUR;

      if (elapsed < p1End) {
        /* ── Phase 1: pulse wave sweeps left → right (max 70%) ── */
        var t1 = elapsed / PHASE_1_DUR;
        var eased = easeInOutSine(t1);

        // Wave left edge goes from 0 to (WAVE_MAX - WAVE_WIDTH)
        // so the right edge goes from WAVE_WIDTH(30%) to WAVE_MAX(70%)
        var leftPos = lerp(0, WAVE_MAX - WAVE_WIDTH, eased);
        pulse.style.left = (leftPos * 100) + '%';

        // Fade in during first 15%, fade out during last 15%
        var fadeIn = clamp01(t1 / 0.15);
        var fadeOut = clamp01((1 - t1) / 0.15);
        pulse.style.opacity = String(Math.min(fadeIn, fadeOut));

        // Last 15%: start transitioning icon from grey to orange
        var handoff = clamp01((t1 - 0.85) / 0.15); // 0 at 85%, 1 at 100%
        if (handoff > 0) {
          button.style.color = BRAND_ORANGE;
          button.style.opacity = String(lerp(0.3, 0.65, handoff));
        } else {
          button.style.color = '';
          button.style.filter = '';
          button.style.opacity = '';
        }

      } else if (elapsed < p2End) {
        /* ── Phase 2: finish icon transition to full orange ── */
        var t2 = (elapsed - p1End) / PHASE_2_DUR;
        var ease2 = easeInOutSine(t2);

        pulse.style.opacity = '0';
        button.style.color = BRAND_ORANGE;
        button.style.opacity = String(lerp(0.65, 1, ease2));
        button.style.filter = '';

      } else if (elapsed < p3End) {
        /* ── Phase 3: icon orange breathing × 3 ── */
        var t3Local = (elapsed - p2End) % PHASE_3_DUR;
        var breathT = t3Local / PHASE_3_DUR;
        // cosine wave: starts at 1 (bright), dips to 0 (dim), back to 1
        // This avoids a jump from Phase 2 which ends at full brightness
        var cosine = 0.5 + 0.5 * Math.cos(breathT * Math.PI * 2);
        var alpha = lerp(0.3, 1, cosine);
        var glowRadius = lerp(2, 6, cosine);
        var glowAlpha = lerp(0.15, 0.5, cosine);

        pulse.style.opacity = '0';
        button.style.color = BRAND_ORANGE;
        button.style.opacity = String(alpha);
        button.style.filter = 'drop-shadow(0 0 ' + glowRadius.toFixed(1) + 'px rgba(' + BRAND_RGB + ',' + glowAlpha.toFixed(2) + '))';

      } else if (elapsed < p4End) {
        /* ── Phase 4: smooth fade back to default ── */
        var t4 = (elapsed - p3End) / PHASE_4_DUR;
        var ease4 = easeInOutSine(t4);

        // Opacity: from where Phase 3 ended (bright=1) back to 1 (full, default)
        // Phase 3 cosine ends at bright (1), so we're already at full opacity
        // We just need to fade out the orange color and glow
        var glowFade = lerp(2, 0, ease4);
        var glowAlphaFade = lerp(0.15, 0, ease4);
        button.style.color = ease4 < 1 ? BRAND_ORANGE : '';
        button.style.opacity = '';
        button.style.filter = glowFade > 0.1
          ? 'drop-shadow(0 0 ' + glowFade.toFixed(1) + 'px rgba(' + BRAND_RGB + ',' + glowAlphaFade.toFixed(2) + '))'
          : '';

        // At the very end, clear inline color to let CSS take over
        if (ease4 >= 0.98) {
          button.style.color = '';
          button.style.filter = '';
        }

      } else {
        /* ── Phase 5: quiet ── */
        resetVisuals();
      }
    }

    rafId = requestAnimationFrame(tick);

    /* cleanup for Theme Editor hot-reload */
    return function destroy() {
      if (rafId) cancelAnimationFrame(rafId);
      if (pulse.parentNode) pulse.parentNode.removeChild(pulse);
      resetVisuals();
    };
  }

  /* ── bootstrap ─────────────────────────────────────────────────── */
  function boot() {
    var destroyers = [];
    var sections = document.querySelectorAll('.cus-search-header');
    for (var i = 0; i < sections.length; i++) {
      var d = initPulse(sections[i]);
      if (d) destroyers.push(d);
    }

    /* Theme Editor: re-init on section reload */
    document.addEventListener('shopify:section:load', function (e) {
      var sec = e.target.querySelector('.cus-search-header');
      if (sec) {
        var d = initPulse(sec);
        if (d) destroyers.push(d);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
